import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "./utils";

interface PopoverContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const PopoverContext = React.createContext<PopoverContextType | null>(null);

function Popover({
  open,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const triggerRef = React.useRef<HTMLElement>(null);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      onOpenChange?.(newOpen);
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
    },
    [onOpenChange, isControlled],
  );

  return (
    <PopoverContext.Provider
      value={{
        open: isOpen,
        onOpenChange: handleOpenChange,
        triggerRef,
      }}
    >
      {children}
    </PopoverContext.Provider>
  );
}

function PopoverTrigger({
  children,
  asChild,
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  asChild?: boolean;
}) {
  const context = React.useContext(PopoverContext);

  if (!context) {
    throw new Error("PopoverTrigger must be used within Popover");
  }

  const handleClick = () => {
    context.onOpenChange(!context.open);
  };

  if (asChild && React.isValidElement(children)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return React.cloneElement(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children as React.ReactElement<any>,
      {
        ref: context.triggerRef,
        ...props,
        onClick: handleClick,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
    );
  }

  return (
    <button
      ref={context.triggerRef as React.RefObject<HTMLButtonElement>}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

function PopoverContent({
  className,
  children,
  portalled = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { portalled?: boolean }) {
  const context = React.useContext(PopoverContext);

  if (!context) {
    throw new Error("PopoverContent must be used within Popover");
  }

  const [style, setStyle] = React.useState<React.CSSProperties>({});

  React.useLayoutEffect(() => {
    if (!context.open || !context.triggerRef.current) return;

    if (!portalled) {
      setStyle({
        position: "absolute",
        top: "calc(100% + 4px)",
        left: 0,
        width: "100%",
        zIndex: 9999,
      });
      return;
    }

    const rect = context.triggerRef.current.getBoundingClientRect();

    setStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.open, portalled]);

  if (!context.open) return null;

  const content = (
    <div
      style={style}
      className={cn(
        "rounded-md border bg-white shadow-lg animate-in fade-in-0 zoom-in-95",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );

  return portalled ? createPortal(content, document.body) : content;
}

export { Popover, PopoverTrigger, PopoverContent };
